import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import { empresaInicial, lembrarEmpresa, comEmpresa } from '../components/empresaLembrada';
import { isQaEnvironment } from '../utils/subdomain';
import Dialogo from '../components/Dialogo';
import './Turmas.css';

/**
 * Detalhe da turma (§9.4/§9.8): membros, situação de cada um e as duas ações do CS.
 *
 * TRÊS COISAS QUE A TELA MOSTRA PORQUE SÃO CONTRAINTUITIVAS:
 *
 * 1. **Loja atual x loja de origem.** Se o membro trocou de loja, ele continua na turma e a linha
 *    ganha um marcador. Não existe ação "mover para a turma da outra loja" de propósito: encerrar
 *    um vínculo e abrir outro reiniciaria a âncora, e o vendedor perderia o progresso da fase por
 *    uma movimentação administrativa. A loja atual é informação, não vínculo.
 *
 * 2. **Início efetivo por membro.** É `GREATEST(âncora da turma, admissão do vendedor)`, então dois
 *    membros da mesma turma podem começar em datas diferentes. Sem dizer isso, parece inconsistência.
 *
 * 3. **Excluir é saída antecipada, não apagar.** O histórico da passagem pela fase fica: os
 *    atendimentos daquele período continuam selados na fase em que foram avaliados. O que termina é
 *    o vínculo, do dia informado em diante.
 */

const brDate = (iso) => {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};
// Data LOCAL, não `toISOString()`: aquele é UTC e, das 21h à meia-noite no Brasil, devolve o dia
// SEGUINTE. Num campo cujo valor é "primeiro dia fora da turma", isso registraria a saída um dia
// depois do pretendido — o mesmo erro de um dia que o `exited_on` já convida a cometer.
const diaLocal = (d = new Date()) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hoje = () => diaLocal();
/** Dias corridos entre duas datas ISO (data pura, sem fuso — `new Date('YYYY-MM-DD')` é UTC). */
const diasEntre = (de, ate) => Math.round(
    (Date.parse(`${ate}T00:00:00`) - Date.parse(`${de}T00:00:00`)) / 86400000);

/** Dia anterior a uma data ISO, para mostrar até quando o membro ainda conta. */
const diaAnterior = (iso) => {
    if (!iso) return iso;
    const [a, m, d] = iso.split('-').map(Number);
    const x = new Date(a, m - 1, d);          // construtor local, sem passar por UTC
    x.setDate(x.getDate() - 1);
    return diaLocal(x);
};

function TurmaDetalhe({ user }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const subdomainQS = params.get('subdomain') || '';

    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 6000);
    }, []);

    const [subdomain, setSubdomain] = useState(() => empresaInicial(subdomainQS));
    const [companies, setCompanies] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [salesmen, setSalesmen] = useState([]);
    const [novoMembro, setNovoMembro] = useState('');
    const [addStore, setAddStore] = useState('');
    const [stores, setStores] = useState([]);
    // Ranking da turma (§9.11) — carregado sob demanda: é uma soma sobre atendimentos, e a maioria
    // das visitas a esta tela é para mexer em membro, não para ver placar.
    const [ranking, setRanking] = useState(null);
    const [carregandoRanking, setCarregandoRanking] = useState(false);
    // §N26 — destrave de feedback/histórico por contagem. Chave: salesman_id.
    const [destrave, setDestrave] = useState({});
    const [destraveOcupado, setDestraveOcupado] = useState(false);

    const verRanking = useCallback(async () => {
        if (ranking) { setRanking(null); return; }   // o botão alterna
        setCarregandoRanking(true);
        try {
            const r = await apiService.cohortRanking({ subdomain, cohort_id: Number(id) });
            setRanking(r?.data || null);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar o ranking da turma', 'error');
        } finally {
            setCarregandoRanking(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ranking, subdomain, id]);

    useEffect(() => {
        (async () => {
            try {
                const lista = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(lista) ? lista : []);
                // NÃO cair na primeira empresa da lista quando a URL não traz subdomain: o id da
                // turma só existe no schema dela, e adivinhar produz "Turma não encontrada" — um
                // erro que aponta para o lugar errado. Sem subdomain, a tela pede.
            } catch (e) { notify('Erro ao carregar empresas', 'error'); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Início da turma que AINDA NÃO COMEÇOU: a API refaz a trilha de cada membro a partir da data
    // nova (respeitando a admissão) e realinha o planejado — a régua passa a mostrar a data nova.
    const [novoInicio, setNovoInicio] = useState(null);
    const mudarInicio = async () => {
        if (!novoInicio) return;
        try {
            const previa = await apiService.phaseCohortSave({ subdomain, id: Number(id), date_start: novoInicio, dry_run: true });
            const mov = previa?.members_moved || [];
            const ok = await confirmar({
                titulo: `Mudar o início da turma para ${brDate(novoInicio)}?`,
                rotuloConfirmar: 'Mudar início',
                largo: mov.length > 0,
                corpo: (
                    <>
                        {mov.length ? (
                            <ul className="dlg-lista">
                                {mov.slice(0, 15).map((m) => (
                                    <li key={m.member_id}>
                                        <strong>{m.salesman_name || m.salesman_id}</strong>: {brDate(m.para.inicio)} → {brDate(m.para.fim)}
                                        <span className="dlg-nota"> (era {brDate(m.de.inicio)} → {brDate(m.de.fim)})</span>
                                        {m.inicio_ajustado_pela_admissao && <span className="dlg-nota"> · início ajustado pela admissão</span>}
                                    </li>
                                ))}
                                {mov.length > 15 && <li>… e mais {mov.length - 15}</li>}
                            </ul>
                        ) : <p>A turma ainda não tem membros.</p>}
                        <p className="dlg-nota">A trilha de todos os membros é refeita a partir da nova data, e o planejado acompanha. A régua passa a mostrar a data nova.</p>
                    </>
                ),
            });
            if (!ok) return;
            await apiService.phaseCohortSave({ subdomain, id: Number(id), date_start: novoInicio });
            setNovoInicio(null);
            notify('Início da turma alterado', 'success');
            carregar();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao mudar o início da turma', 'error');
        }
    };

    const carregar = useCallback(async () => {
        if (!subdomain || !id) return;
        setLoading(true);
        try {
            const r = await apiService.phaseCohortDetail({ subdomain, cohort_id: Number(id) });
            setData(r?.data || null);
            if (r?.data?.cohort?.origin_store?.id) setAddStore(String(r.data.cohort.origin_store.id));
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao carregar a turma', 'error');
        } finally { setLoading(false); }
    }, [subdomain, id, notify]);

    useEffect(() => { carregar(); }, [carregar]);

    // Só há o que mostrar quando ALGUMA fase da turma tem a regra ligada; a lista volta vazia nos
    // outros casos e a coluna some sozinha. Por isso a chamada é solta e silenciosa: nada aqui
    // pode impedir a turma de abrir.
    const carregarDestrave = useCallback(async () => {
        if (!subdomain || !id) return;
        try {
            const r = await apiService.phaseUnlockList({ subdomain, cohort_id: Number(id) });
            const porVendedor = {};
            (r?.data?.members || []).forEach((m) => { porVendedor[m.salesman_id] = m; });
            setDestrave(porVendedor);
        } catch (e) { /* silencioso de propósito */ }
    }, [subdomain, id]);

    useEffect(() => { carregarDestrave(); }, [carregarDestrave]);

    const reavaliarDestrave = async () => {
        setDestraveOcupado(true);
        try {
            const r = await apiService.phaseUnlockEvaluate({ subdomain, cohort_id: Number(id) });
            const gravados = r?.data?.written || 0;
            notify(gravados
                ? `${gravados} vendedor(es) destravaram feedback agora`
                : 'Ninguém novo atingiu o limite', gravados ? 'success' : 'info');
            carregarDestrave();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao reavaliar o destrave', 'error');
        } finally { setDestraveOcupado(false); }
    };

    useEffect(() => {
        if (!subdomain) return;
        lembrarEmpresa(subdomain);
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { /* silencioso: sem lojas o seletor fica vazio */ }
        })();
    }, [subdomain]);

    // Vendedores da loja escolhida para incluir. A turma tem loja de origem, mas incluir de outra
    // loja é permitido: a fase é do programa. O seletor de loja aqui é conveniência, não regra.
    useEffect(() => {
        setSalesmen([]); setNovoMembro('');
        if (!subdomain || !addStore) return;
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({
                    userId: user?.user_id, subdomain, store_id: Number(addStore),
                });
                setSalesmen(r?.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
    }, [subdomain, addStore, user]);

    const incluir = async () => {
        if (!novoMembro) return notify('Escolha um vendedor', 'error');
        try {
            const r = await apiService.phaseMemberAdd({
                subdomain, cohort_id: Number(id), salesman_id: Number(novoMembro),
            });
            const d = r?.data;
            notify(
                d?.anchor_shifted_by_admission
                    ? `Incluído. O início foi ajustado para ${brDate(d.date_start)}: a fase não começa antes da admissão do vendedor.`
                    : `Incluído a partir de ${brDate(d?.date_start)}.`,
                'success',
            );
            setNovoMembro('');
            carregar();
        } catch (e) {
            // Erros de negócio esperados aqui: vendedor já em turma sobreposta (409), vendedor sem
            // data de admissão (400), pessoa com mais de um vínculo ativo (409). O texto da API é
            // que orienta, então mostro ele.
            notify(e?.response?.data?.detail || 'Erro ao incluir membro', 'error');
        }
    };

    // ---------------------------------------------------------------- simulação (§9.14, só QA)
    // Existe porque não há job de transição: a trilha inteira é materializada na inclusão, então
    // "avançar de fase" é o calendário passando. Sem uma ferramenta, testar transição significa
    // esperar dias ou mexer em data no banco.
    const ehQa = isQaEnvironment();
    const [sim, setSim] = useState(null);        // { membro, dia, resultado } — painel aberto
    const [simOcupado, setSimOcupado] = useState(false);
    const [confirma, setConfirma] = useState(null);  // { tipo: 'avancar'|'refazer', membro }

    const simular = async () => {
        setSimOcupado(true);
        try {
            const r = await apiService.phaseResolve({
                subdomain, salesman_id: sim.membro.salesman_id, at_date: sim.dia,
            });
            setSim((s) => ({ ...s, resultado: r?.data || r }));
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao simular', 'error');
        } finally { setSimOcupado(false); }
    };

    const executarSimulacao = async () => {
        const { tipo, membro } = confirma;
        setSimOcupado(true);
        try {
            if (tipo === 'avancar') {
                const r = await apiService.phaseMemberAdvance({ subdomain, member_id: membro.member_id, advance_on: hoje() });
                const d = r?.data || r;
                // A API decide entre avançar e PULAR pela data (fase que começou hoje é pulada,
                // porque fase de zero dia não existe). Quem conta o que aconteceu é ela, não a tela.
                notify(d?.action === 'skipped'
                    ? `${membro.salesman_name} PULOU a fase — ela saiu da trilha e as `
                      + `${d.phases_shifted} seguintes voltaram ${d.skipped_days} dia(s).`
                    : `${membro.salesman_name} avançou de fase hoje. As fases seguintes andaram junto.`,
                    'success');
            } else {
                const r = await apiService.phaseRebuildTrail({ subdomain, member_id: membro.member_id });
                const d = r?.data || r;
                notify(`Trilha refeita: ${d.windows_created} janelas pelo plano`
                       + (d.reseal ? `, ${d.reseal.sealed} selo(s) reescrito(s).` : '.'), 'success');
            }
            setConfirma(null);
            setSim(null);
            carregar();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro na simulação', 'error');
        } finally { setSimOcupado(false); }
    };

    // ------------------------------------------------------------- reaplicar plano (§2.11)
    // Preview PRIMEIRO, sempre: a ação mexe na trilha de todo mundo da turma, e o §2.11 exige que
    // o CS veja o efeito que chega no vendedor (quando o programa termina, quando o placar abre)
    // antes de confirmar.
    const [replan, setReplan] = useState(null);      // { preview } | null
    const [replanOcupado, setReplanOcupado] = useState(false);

    const previewReplan = async () => {
        setReplanOcupado(true);
        try {
            const r = await apiService.phaseReapplyPlan({ subdomain, cohort_id: Number(id), dry_run: true });
            setReplan({ preview: r?.data || r });
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao calcular o preview', 'error');
        } finally { setReplanOcupado(false); }
    };

    const aplicarReplan = async () => {
        setReplanOcupado(true);
        try {
            const r = await apiService.phaseReapplyPlan({ subdomain, cohort_id: Number(id), dry_run: false });
            const d = r?.data || r;
            notify(`Plano reaplicado: ${d.summary.fim_alterado + d.summary.reescritas} janela(s) `
                   + `alterada(s) em ${d.summary.members} membro(s).`, 'success');
            setReplan(null);
            carregar();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao reaplicar o plano', 'error');
        } finally { setReplanOcupado(false); }
    };

    // Saída antecipada: diálogo próprio, não window.prompt. A data pedida é o PRIMEIRO DIA FORA, e
    // essa é a parte que engana — por isso o diálogo mostra, enquanto a pessoa escolhe, até quando o
    // vendedor ainda conta. Um prompt do browser não tem onde dizer isso.
    const [saida, setSaida] = useState(null);      // { membro, dia }
    const [salvandoSaida, setSalvandoSaida] = useState(false);

    const confirmarSaida = async () => {
        if (!saida?.dia) return;
        setSalvandoSaida(true);
        try {
            await apiService.phaseMemberRemove({
                subdomain, member_id: saida.membro.member_id, exit_on: saida.dia,
            });
            notify(`${saida.membro.salesman_name} sai da turma em ${brDate(saida.dia)}. O histórico foi preservado.`, 'success');
            setSaida(null);
            carregar();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao registrar a saída', 'error');
        } finally {
            setSalvandoSaida(false);
        }
    };

    const c = data?.cohort;
    const ativos = (data?.members || []).filter((m) => !m.exited_early_on);
    // A coluna de destrave só existe se ALGUMA fase desta turma tem a regra ligada (§N26) — em
    // turma sem a regra a lista volta vazia e a tela fica idêntica à de hoje.
    const temDestrave = Object.keys(destrave).length > 0;
    const saidos = (data?.members || []).filter((m) => m.exited_early_on);

    return (
        <div className="turmas-page">
            {toast && <div className={`turmas-toast ${toast.type}`}>{toast.msg}</div>}

            {/* PAINEL DE SIMULAÇÃO — §9.14, só em QA. A trava que vale é a da API (APP_ENV):
                esconder o botão não protege rota, e as duas ações de escrita respondem 403 fora
                de QA mesmo que alguém chame o endpoint na mão. */}
            {sim && (
                <div className="turmas-sim">
                    <div className="turmas-sim-head">
                        <strong>🧪 Simulação de fase — {sim.membro.salesman_name}</strong>
                        <button className="turmas-btn" onClick={() => setSim(null)}>Fechar</button>
                    </div>

                    <div className="turmas-sim-linha">
                        <label>Ver como fica em
                            <input type="date" value={sim.dia}
                                onChange={(e) => setSim((s) => ({ ...s, dia: e.target.value, resultado: null }))} />
                        </label>
                        <button className="turmas-btn turmas-btn-primary" disabled={simOcupado} onClick={simular}>
                            {simOcupado ? 'Consultando…' : 'Simular'}
                        </button>
                        <span className="turmas-sim-nota">não grava nada — é a resolução na data escolhida</span>
                    </div>

                    {sim.resultado && (
                        <div className="turmas-sim-saida">
                            <p>
                                Em <strong>{brDate(sim.dia)}</strong>:{' '}
                                {sim.resultado.in_phase
                                    ? <>fase <strong>{sim.resultado.phase_name}</strong>
                                        {/* o /phases/resolve não devolve `day_of_phase` (ele é calculado no
                                            current_phases_batch), então o dia sai daqui: dias corridos desde a
                                            entrada, 1-based — a mesma conta que a API faz. */}
                                        {sim.resultado.entered_on
                                            ? ` — dia ${diasEntre(sim.resultado.entered_on, sim.dia) + 1}`
                                            + (sim.resultado.duration_days ? ` de ${sim.resultado.duration_days}` : '')
                                            : ''}
                                        {sim.resultado.exited_on
                                            ? <> (até {brDate(diaAnterior(sim.resultado.exited_on))})</> : null}
                                        {sim.resultado.locks_scoreboard ? ' · placar fechado' : ' · placar aberto'}</>
                                    : <><strong>fora de programa</strong> (ongoing)</>}
                            </p>
                        </div>
                    )}

                    <div className="turmas-sim-acoes">
                        <button className="turmas-btn turmas-btn-perigo" disabled={simOcupado}
                            onClick={() => setConfirma({ tipo: 'avancar', membro: sim.membro })}>
                            Avançar fase agora
                        </button>
                        <button className="turmas-btn" disabled={simOcupado}
                            onClick={() => setConfirma({ tipo: 'refazer', membro: sim.membro })}>
                            Refazer trilha pelo plano
                        </button>
                    </div>

                    <p className="turmas-sim-rodape">
                        <strong>O app não vira na hora.</strong> Telas de dados (pontos, metas, ranking, comunicados)
                        atualizam no próximo carregamento; o <strong>placar</strong> abre/fecha no próximo
                        <code> /auth</code> (login); e o <strong>tema da fase</strong> só troca quando o app
                        reabre — o config é buscado no boot. Para ver a virada completa no aparelho, feche e
                        abra o app.
                    </p>
                </div>
            )}

            {confirma && (
                <Dialogo
                    titulo={confirma.tipo === 'avancar'
                        ? `Avançar ${confirma.membro.salesman_name} de fase hoje?`
                        : `Refazer a trilha de ${confirma.membro.salesman_name}?`}
                    perigo={confirma.tipo === 'avancar'}
                    ocupado={simOcupado}
                    rotuloConfirmar={confirma.tipo === 'avancar' ? 'Avançar' : 'Refazer'}
                    rotuloOcupado="Executando…"
                    onCancelar={() => setConfirma(null)}
                    onConfirmar={executarSimulacao}
                >
                    {confirma.tipo === 'avancar' ? (
                        <>
                            <p className="dlg-efeito">
                                A fase vigente <strong>encerra hoje</strong> e todas as seguintes andam junto,
                                mantendo a duração de cada uma.
                            </p>
                            <p className="dlg-nota">
                                <strong>Se a fase vigente começou hoje</strong> — porque o membro entrou hoje, ou
                                já foi avançado hoje — ela é <strong>pulada</strong>: sai da trilha e as seguintes
                                voltam pela duração inteira dela. Não existe fase de zero dia, então o histórico
                                passa a dizer que ele não fez essa fase. Fase que já tem atendimento gravado
                                nela não pode ser pulada.
                            </p>
                            <p className="dlg-nota">
                                Fica gravado como decisão humana (<code>source='forced'</code>), com quem
                                clicou. <strong>Não tem desfazer automático</strong> — o caminho de volta é o
                                "Refazer trilha pelo plano", aqui do lado.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="dlg-efeito">
                                A trilha volta ao <strong>plano do programa</strong>: as janelas atuais são
                                descartadas e recalculadas a partir do início efetivo do membro.
                            </p>
                            <p className="dlg-nota">
                                Os selos dos atendimentos dele são <strong>reescritos</strong> pela trilha nova, para
                                que os números não fiquem apontando para um estado que não existe mais.
                                É destrutivo de propósito e por isso só existe em QA — em produção o caminho é
                                reaplicar plano, que preserva janela encerrada.
                            </p>
                        </>
                    )}
                </Dialogo>
            )}

            {/* PREVIEW do reaplicar plano (§2.11). O que o CS precisa ver antes de confirmar não é
                "quantas linhas mudam" — é o efeito no vendedor: quando o programa dele termina e
                quando o placar abre. Os dois saem no preview, por membro. */}
            {replan && (
                <Dialogo
                    titulo="Reaplicar o plano do programa nesta turma"
                    ocupado={replanOcupado}
                    rotuloConfirmar={`Aplicar em ${replan.preview.summary.members} membro(s)`}
                    rotuloOcupado="Aplicando…"
                    confirmarHabilitado={(replan.preview.summary.fim_alterado
                        + replan.preview.summary.reescritas
                        + replan.preview.summary.criadas
                        + replan.preview.summary.removidas) > 0}
                    onCancelar={() => setReplan(null)}
                    onConfirmar={aplicarReplan}
                >
                    <p className="dlg-efeito">
                        <strong>{replan.preview.summary.mantidas}</strong> janela(s) encerrada(s) ficam
                        intocadas · <strong>{replan.preview.summary.fim_alterado}</strong> têm o fim
                        recalculado · <strong>{replan.preview.summary.reescritas}</strong> futuras são
                        reescritas
                        {replan.preview.summary.criadas ? ` · ${replan.preview.summary.criadas} criada(s)` : ''}
                        {replan.preview.summary.removidas ? ` · ${replan.preview.summary.removidas} removida(s)` : ''}.
                    </p>

                    <ul className="turmas-replan-lista">
                        {replan.preview.members.map((m) => {
                            const mudou = m.changes.filter((x) => x.acao !== 'mantida');
                            return (
                                <li key={m.member_id}>
                                    <strong>{m.salesman_name}</strong>
                                    {m.expected_end_on.de !== m.expected_end_on.para ? (
                                        <span className="turmas-replan-efeito">
                                            termina {m.expected_end_on.para ? brDate(m.expected_end_on.para) : 'sem data'}
                                            {' '}(era {m.expected_end_on.de ? brDate(m.expected_end_on.de) : 'sem data'})
                                        </span>
                                    ) : <span className="turmas-replan-igual">sem mudança de fim</span>}
                                    {m.scoreboard_opens_on && (
                                        <span className="turmas-replan-placar">
                                            placar abre {brDate(m.scoreboard_opens_on)}
                                        </span>
                                    )}
                                    {mudou.length > 0 && (
                                        <span className="turmas-replan-detalhe">
                                            {mudou.map((x) => `${x.name || 'fase'}: `
                                                + (x.para
                                                    ? `${brDate(x.para.entered_on)}→${x.para.exited_on ? brDate(x.para.exited_on) : 'sem fim'}`
                                                    : 'removida')).join(' · ')}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    <p className="dlg-nota">
                        Janela <strong>já encerrada não é tocada</strong> — os atendimentos dela estão
                        selados e pontuados com a régua daquela fase. Da fase <strong>vigente</strong> só o
                        fim muda, e fim no passado é grampeado em hoje, para não jogar dias já trabalhados
                        para a fase seguinte. Fica registrado quem aplicou.
                    </p>
                </Dialogo>
            )}

            {saida && (
                <Dialogo
                    titulo={`Saída antecipada de ${saida.membro.salesman_name}`}
                    perigo
                    ocupado={salvandoSaida}
                    confirmarHabilitado={!!saida.dia}
                    rotuloConfirmar="Registrar saída"
                    onCancelar={() => setSaida(null)}
                    onConfirmar={confirmarSaida}
                >
                    <label htmlFor="dlg-saida">Primeiro dia FORA da turma</label>
                    <input id="dlg-saida" type="date" value={saida.dia}
                        onChange={(e) => setSaida((s) => ({ ...s, dia: e.target.value }))} />

                    {saida.dia && (
                        <p className="dlg-efeito">
                            Ele conta na turma <strong>até {brDate(diaAnterior(saida.dia))}</strong> e
                            deixa de contar em <strong>{brDate(saida.dia)}</strong>.
                        </p>
                    )}

                    <p className="dlg-nota" style={{ marginTop: 10 }}>
                        O histórico da passagem dele pelas fases é <strong>preservado</strong> — os atendimentos
                        já avaliados continuam valendo o que valeram. As fases futuras dele nesta turma são
                        descartadas.
                    </p>
                </Dialogo>
            )}

            <header className="turmas-header">
                <div>
                    <button className="turmas-btn turmas-voltar" onClick={() => navigate(comEmpresa('/turmas', subdomain))}>← Turmas</button>
                    <h1>{c?.name || `Turma ${id}`}</h1>
                    {c && (
                        <p className="turmas-sub">
                            {c.program?.name} · loja de origem <strong>{c.origin_store?.name || '—'}</strong> ·
                            início {brDate(c.date_start)}
                            {' '}·
                            {c.anchor === 'member' ? ' turma perene' : ' coorte fechada'} ·
                            {' '}{data?.phases_total} fases
                        </p>
                    )}
                </div>
                {c && c.anchor === 'cohort' && String(c.date_start).slice(0, 10) > new Date().toISOString().slice(0, 10) && (
                    // Turma que AINDA NÃO COMEÇOU: o início pode mudar, e a trilha dos membros vai junto.
                    novoInicio === null ? (
                        <button className="turmas-btn" onClick={() => setNovoInicio(String(c.date_start).slice(0, 10))}
                            title="A turma ainda não começou: dá para mudar o início. A trilha dos membros e a régua acompanham.">
                            📅 Alterar início
                        </button>
                    ) : (
                        <span className="turmas-inicio-edicao">
                            <input type="date" value={novoInicio} min={new Date().toISOString().slice(0, 10)}
                                onChange={(e) => setNovoInicio(e.target.value)} aria-label="Novo início da turma" />
                            <button className="turmas-btn" onClick={mudarInicio}>Salvar início</button>
                            <button className="turmas-btn" onClick={() => setNovoInicio(null)}>Cancelar</button>
                        </span>
                    )
                )}
                {c && (
                    <button className="turmas-btn" disabled={replanOcupado} onClick={previewReplan}
                        title="Recalcula a trilha de todos os membros pelo desenho atual do programa. Mostra o preview antes de gravar.">
                        {replanOcupado && !replan ? 'Calculando…' : '↻ Reaplicar plano'}
                    </button>
                )}
                {(!subdomainQS || !subdomain) && (
                    <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        <option value="">— Empresa —</option>
                        {companies.map((cc) => (
                            <option key={cc.subdomain} value={cc.subdomain}>{cc.name || cc.subdomain}</option>
                        ))}
                    </select>
                )}
            </header>

            {!subdomain && (
                <p className="turmas-vazio">
                    Selecione a empresa desta turma. O identificador da turma só existe no schema da
                    empresa dela, então não dá para adivinhar.
                </p>
            )}
            {loading && <p className="turmas-vazio">Carregando…</p>}

            {data && data.members_in_other_store > 0 && (
                <div className="turmas-aviso">
                    {data.members_in_other_store} de {ativos.length} membros estão hoje em <strong>outra loja</strong>.
                    O vínculo continua valendo: a fase é do programa, não da loja. Mover o membro para outra
                    turma reiniciaria a trilha e apagaria o progresso dele.
                </div>
            )}

            {data && (
                <>
                    <div className="turmas-form">
                        <div className="turmas-form-grid">
                            <div className="turmas-field">
                                <label>Loja (para listar vendedores)</label>
                                <select value={addStore} onChange={(e) => setAddStore(e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                                </select>
                            </div>
                            <div className="turmas-field">
                                <label>Vendedor</label>
                                <select value={novoMembro} onChange={(e) => setNovoMembro(e.target.value)} disabled={!addStore}>
                                    <option value="">— Selecione —</option>
                                    {salesmen.map((s) => (
                                        <option key={s.salesman_id} value={s.salesman_id}>
                                            {s.salesman_name}{s.is_active ? '' : ' (inativo)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="turmas-field turmas-field-acao">
                                <button className="turmas-btn-primary" onClick={incluir} disabled={!novoMembro}>
                                    Incluir na turma
                                </button>
                            </div>
                        </div>
                        <p className="turmas-nota">
                            O início de cada membro é a âncora da turma, <strong>ou a admissão dele</strong> se for
                            mais tarde — a fase não começa antes de o vendedor existir.
                        </p>
                    </div>

                    <div className="turmas-lista">
                        <div className="td-ranking-head">
                            <h2>Ranking da turma</h2>
                            <button className="turmas-btn" onClick={verRanking} disabled={carregandoRanking}>
                                {carregandoRanking ? 'carregando…' : ranking ? 'Ocultar' : '🏆 Ver ranking'}
                            </button>
                        </div>

                        {ranking && (
                            <>
                                {/* O aviso vem primeiro porque muda como se LÊ a tabela: membros em
                                    fases diferentes podem ter réguas diferentes, e aí a comparação
                                    por pontos absolutos fica torta (§N21). */}
                                {!ranking.mesma_fase && (
                                    <p className="td-ranking-aviso">
                                        ⚠️ Os membros <strong>não estão todos na mesma fase</strong> — quem entrou
                                        depois tem a trilha deslocada. Compare com cuidado: fases diferentes podem
                                        ter pontuação diferente.
                                    </p>
                                )}
                                {ranking.stores_distintas > 1 && (
                                    <p className="td-ranking-nota">
                                        Esta turma atravessa <strong>{ranking.stores_distintas} lojas</strong> — a coluna
                                        Loja mostra de onde cada um é.
                                    </p>
                                )}
                                <table className="turmas-table">
                                    <thead>
                                        <tr>
                                            <th>#</th><th>Vendedor</th><th>Loja</th><th>Fase</th>
                                            <th>Atendimentos</th><th>Pontos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(ranking.members || []).map((m) => (
                                            <tr key={m.salesman_id}>
                                                <td className="td-pos">{m.position}</td>
                                                <td>{m.salesman_name}</td>
                                                <td>{m.store_name}</td>
                                                <td>
                                                    {m.phase
                                                        ? `${m.phase.sort_order}. ${m.phase.name}${m.phase.locks_scoreboard ? ' 🔒' : ''}`
                                                        : '—'}
                                                </td>
                                                <td className="td-num">{m.attendances}</td>
                                                <td className="td-num"><strong>{m.points}</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="turmas-nota">
                                    Soma só os atendimentos avaliados <strong>dentro das fases desta turma</strong> —
                                    é o oposto do ranking da loja, que exclui justamente esses. 🔒 marca fase que
                                    tira do ranking da loja.
                                </p>
                            </>
                        )}
                    </div>

                    <div className="turmas-lista">
                        <h2>
                            Membros ({ativos.length})
                            {temDestrave && (
                                <button className="turmas-btn" disabled={destraveOcupado}
                                    style={{ marginLeft: 12, fontWeight: 400 }}
                                    title="Reconta os atendimentos válidos e grava a data de quem já atingiu o limite. Idempotente: quem já destravou não é reescrito."
                                    onClick={reavaliarDestrave}>
                                    {destraveOcupado ? 'Reavaliando…' : '↻ Reavaliar destrave'}
                                </button>
                            )}
                        </h2>
                        {!ativos.length && <p className="turmas-vazio">Nenhum membro ativo nesta turma.</p>}
                        {ativos.length > 0 && (
                            <table className="turmas-table">
                                <thead>
                                    <tr>
                                        <th>Vendedor</th><th>Loja atual</th><th>Início efetivo</th>
                                        <th>Fase vigente</th><th>Dia</th>
                                        {temDestrave && <th>Feedback</th>}
                                        <th>Fim previsto</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ativos.map((m) => (
                                        <tr key={m.member_id}>
                                            <td className="turmas-nome">{m.salesman_name || `#${m.salesman_id}`}</td>
                                            <td>
                                                {m.current_store?.name || '—'}
                                                {m.in_other_store && (
                                                    <span className="turmas-tag" title="Trocou de loja e continua na turma: a fase é do programa.">
                                                        outra loja
                                                    </span>
                                                )}
                                            </td>
                                            <td>{brDate(m.date_start)}</td>
                                            <td>
                                                {m.phase
                                                    ? <>{m.phase.sort_order}. {m.phase.name}
                                                        {m.phase.locks_scoreboard && (
                                                            <span className="turmas-tag turmas-tag-trava"
                                                                title="Nesta fase o placar individual fecha e o vendedor sai do ranking.">
                                                                placar travado
                                                            </span>
                                                        )}
                                                    </>
                                                    : <em>fora de fase</em>}
                                            </td>
                                            <td>
                                                {m.phase?.day_of_phase != null && m.phase?.duration_days
                                                    ? `${m.phase.day_of_phase} de ${m.phase.duration_days}`
                                                    : '—'}
                                            </td>
                                            {temDestrave && (
                                                <td>
                                                    {(() => {
                                                        const d = destrave[m.salesman_id];
                                                        if (!d) return '—';
                                                        if (d.unlocked) {
                                                            return (
                                                                <span className="turmas-tag turmas-tag-ok"
                                                                    title={`Destravou com ${d.threshold_at_unlock || d.threshold} atendimentos válidos`}>
                                                                    ✓ {brDate(d.unlocked_on)}
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <span title="Enquanto não atingir, o vendedor não vê feedback nem histórico no app.">
                                                                {d.count}/{d.threshold}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                            )}
                                            <td>{m.expected_end_on ? brDate(m.expected_end_on) : <em>sem fim planejado</em>}</td>
                                            <td>
                                                <button className="turmas-btn" onClick={() => setSaida({ membro: m, dia: hoje() })}>Saída</button>
                                                {ehQa && (
                                                    <button className="turmas-btn turmas-btn-qa"
                                                        title="Ferramentas de simulação de fase — só existem em QA"
                                                        onClick={() => setSim({ membro: m, dia: hoje(), resultado: null })}>
                                                        🧪 Simular
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {saidos.length > 0 && (
                        <div className="turmas-lista">
                            <h2>Saíram ({saidos.length})</h2>
                            <p className="turmas-nota">
                                O vínculo terminou, mas o histórico ficou: os atendimentos do período continuam
                                selados na fase em que foram avaliados.
                            </p>
                            <table className="turmas-table">
                                <thead>
                                    <tr><th>Vendedor</th><th>Início</th><th>Saiu em</th></tr>
                                </thead>
                                <tbody>
                                    {saidos.map((m) => (
                                        <tr key={m.member_id} className="turmas-linha-saiu">
                                            <td>{m.salesman_name || `#${m.salesman_id}`}</td>
                                            <td>{brDate(m.date_start)}</td>
                                            <td>{brDate(m.exited_early_on)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TurmaDetalhe;
